import { FC, createRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import * as yup from "yup";
import ModalComponent from "../../../../core/components/modal/modal.component";
import TableComponent from "../../../../core/components/table/table.component";
import FilterFormComponent, {
  FilterFormConfig,
} from "../../../../shared/filter-form/filter-form.component";
import FormBuilderComponent, {
  FormConfig,
} from "../../../../shared/form-builder/form-builder.component";
import { getBlocksBySiteCode } from "../../../../store/slices/location-block.slice";
import { getSiteByTCCode } from "../../../../store/slices/location-site.slice";
import {
  addNewSchedule,
  getTestTypes,
  sendLightTestScheduleToDevice,
  setScheduleFilter,
} from "../../../../store/slices/test.slice";
import { getTownCouncils } from "../../../../store/slices/town-council.slice";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import "./schedule.scss";
import { B2Button } from "@hdb/biome-2";

interface NewScheduleTableComponentProps {
  columns: any;
  data: any[];
  totalNumberOfRecords: number;
  pageSize: number;
  current: number;
  onChangePage: any;
  onChangeSort: any;
  onRefetchData: (
    pageNumber: number,
    tcCode: any,
    siteCode: any,
    blockCode: any,
    testTypeId: any
  ) => void;
  isLoading: boolean;
  getTestType: (testTypeId: any) => void;
}

const NewScheduleTableComponent: FC<NewScheduleTableComponentProps> = ({
  columns,
  data,
  totalNumberOfRecords,
  pageSize,
  current,
  onChangePage,
  onChangeSort,
  onRefetchData,
  getTestType,
  isLoading,
}) => {
  const formRef = createRef();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [tcOptions, setTCOptions] = useState<any>([]);
  const [siteOptions, setSiteOptions] = useState<any>([]);
  const [blockOptions, setBlockOptions] = useState<any>([]);
  const [isFiltered, setIsFiltered] = useState<any>(false);
  const [testTypeOptions, setTestTypeOptions] = useState<any>([]);
  const [selectedTestType, setSelectedTestType] = useState<any>(null);
  const [modalFormInitialValues, setModalFormInitialValues] = useState<any>({
    functionalTestDateTime: "",
    durationTestDateTime: "",
  });

  const [filterUpdateCount, setFilterUpdateCount] = useState(0);

  const dispatch = useDispatch();

  const selectedItems = useSelector((state: any) => state.table.selectedItems);

  useEffect(() => {
    dispatch(getTownCouncils({ PageNumber: 0, pageSize: 0 }));
    dispatch(getTestTypes());
    dispatch({
      type: "table/toggleSelectAll",
      payload: [],
    });
  }, [dispatch]);

  const townCouncilData = useSelector((state: any) => {
    return state.townCouncil.townCouncils;
  });

  const isLoadingTownCouncils = useSelector((state: any) => {
    return state.townCouncil.isLoadingTownCouncils;
  });

  const siteDataByTCCode = useSelector((state: any) => {
    return state.locationSite.sitesByTCCode;
  });

  const isLocationSitesByTCCode = useSelector((state: any) => {
    return state.locationSite.isLocationSitesByTCCode;
  });

  const blockDataBySiteCode = useSelector((state: any) => {
    return state.locationBlock.blocksBySiteCode;
  });

  const isLoadingBlocksBySiteCode = useSelector((state: any) => {
    return state.locationBlock.isLoadingBlocksBySiteCode;
  });

  const testTypesData = useSelector((state: any) => {
    return state.test.testTypes;
  });

  const isLoadingTestTypes = useSelector((state: any) => {
    return state.test.isLoadingTestTypes;
  });
  const [currentTC, setCurrentTC] = useState({ label: "", value: "" });

  useEffect(() => {
    const tcs = townCouncilData?.data?.map((tc: any) => ({
      label: tc?.name,
      value: tc?.tcCode,
    }));
    // if user is TC Admin only 1 tc is available
    if (tcs?.length === 1) {
      setCurrentTC(tcs[0]);
    }
    setTCOptions(tcs);
    setFilterUpdateCount(filterUpdateCount + 1);
  }, [townCouncilData]);

  useEffect(() => {
    if (currentTC.value.length) {
      setFormFilterInitialValues((prev) => ({
        ...prev,
        townCouncil: currentTC.value,
      }));

      dispatch(getSiteByTCCode({ tcCode: currentTC.value }));
    }
  }, [currentTC]);

  useEffect(() => {
    const sites = siteDataByTCCode?.data?.map((site: any) => ({
      label: site?.name,
      value: site?.siteCode,
    }));

    if (!sites?.length) return;

    setSiteOptions(sites);
  }, [siteDataByTCCode]);

  useEffect(() => {
    const blocks = blockDataBySiteCode?.data?.map((block: any) => ({
      label: block?.blockName,
      value: block?.blockCode,
    }));

    setBlockOptions(blocks);
  }, [blockDataBySiteCode]);

  useEffect(() => {
    const testTypes = testTypesData?.data?.map((test: any) => ({
      label: test?.name,
      value: test?.id,
    }));

    setTestTypeOptions(testTypes);
  }, [testTypesData]);

  const onChangeTC = (selectedTC: any) => {
    setSiteOptions([]);
    setBlockOptions([]);

    const form: any = formRef.current;

    if (form) {
      // Get all field names except 'towncouncil'
      const fieldNames = Object.keys(form.values).filter(
        (name) => name !== "towncouncil"
      );

      // Reset each field to empty, except 'towncouncil'
      fieldNames.forEach((fieldName) => {
        if (fieldName === "site" || fieldName === "block") {
          form.setFieldValue(fieldName, "", false); // false to avoid validation after setting field
        }
      });
    }

    dispatch(getSiteByTCCode({ tcCode: selectedTC }));
  };

  const onChangeSite = (selectedSite: any) => {
    setBlockOptions([]);

    dispatch(getBlocksBySiteCode({ siteCode: selectedSite }));
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const formConfig: FormConfig = {
    controls: [
      {
        name: "functionalTestDateTime",
        label: "Functional Test Date and Time",
        type: "datetime",
        required: true,
      },
      {
        name: "durationTestDateTime",
        label: "Duration Test Date and Time",
        type: "datetime",
        required: true,
      },
    ],
  };

  const validationSchema = yup.object().shape({
    functionalTestDateTime: yup
      .date()
      .required("Functional Test & Date is required"),
    durationTestDateTime: yup
      .date()
      .required("Duration Test Date & Time is required"),
  });

  const onSubmit = () => {
    const form: any = formRef.current;

    const ids = selectedItems?.map((item: any) => item?.value);

    const payload = {
      functionalTestDateTime: form?.values?.functionalTestDateTime,
      durationTestDateTime: form?.values?.durationTestDateTime,
      sensorIds: ids,
    };

    if (form) {
      form.handleSubmit();
      dispatch(addNewSchedule({ payload })).then(() => {
        handleCloseModal();
        dispatch(sendLightTestScheduleToDevice({ payload: ids }));
        navigate("/test");
      });
    }
  };

  const modalActionConfig: any = [
    {
      label: "Set",
      event: onSubmit,
      variant: "primary",
    },
  ];

  const onSubmitFilter = () => {
    const form: any = formRef.current;

    if (form) {
      form.handleSubmit();

      let { townCouncil, site, block, testType } = form.values;

      if (testType) {
        setSelectedTestType(testType);

        setModalFormInitialValues({
          functionalTestDateTime: "",
          durationTestDateTime: "",
        });

        getTestType(testType);
      } else {
        return;
      }

      if (townCouncil || site || block || testType) {
        dispatch(setScheduleFilter(form.values));
      }

      setIsFiltered(true);

      dispatch({
        type: "table/toggleSelectAll",
        payload: [],
      });
    }
  };

  const onReset = () => {
    const form: any = formRef.current;
    form.resetForm();

    setFilterUpdateCount(filterUpdateCount + 1);

    dispatch(setScheduleFilter({}));

    setIsFiltered(false);
    setSelectedTestType(null);

    setModalFormInitialValues({
      functionalTestDateTime: "",
      durationTestDateTime: "",
    });

    getTestTypes(null);
    setSiteOptions([]);
    setBlockOptions([]);
  };

  const filterActionConfig: any = {
    label: "Filter",
    event: onSubmitFilter,
    variant: "primary",
  };

  const reset: any = {
    label: "Reset",
    event: onReset,
    variant: "secondary",
  };

  const [formFilterInitialValues, setFormFilterInitialValues] = useState({
    townCouncil: "",
    site: "",
    block: "",
    testType: "",
  });

  // const formFilterInitialValues = {
  //   townCouncil: "",
  //   site: "",
  //   block: "",
  //   testType: "",
  // };

  const filterFormConfig: FilterFormConfig = {
    controls: [
      {
        controlGroup: [
          {
            name: "testType",
            placeholder: "Select Test Type *",
            type: "select",
            options: testTypeOptions,
            required: true,
            isLoading: isLoadingTestTypes,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "townCouncil",
            placeholder: "Select Town Council",
            type: "select",
            options: tcOptions,
            onChange: onChangeTC,
            required: true,
            isLoading: isLoadingTownCouncils,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "site",
            placeholder: "Select Site",
            type: "select",
            options: siteOptions,
            onChange: onChangeSite,
            required: true,
            isLoading: isLocationSitesByTCCode,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "block",
            placeholder: "Select Block",
            type: "select",
            options: blockOptions,
            required: true,
            isLoading: isLoadingBlocksBySiteCode,
          },
        ],
      },
    ],
  };

  const filterValidationSchema = yup.object().shape({
    testType: yup.number().required("Test Type is required"),
  });

  return (
    <div>
      <div>
        <div>
          <FilterFormComponent
            key={formFilterInitialValues?.townCouncil}
            formRef={formRef}
            initialValues={formFilterInitialValues}
            formConfig={filterFormConfig}
            actionConfig={filterActionConfig}
            validation={filterValidationSchema}
            resetButton={reset}
            filterUpdateCount={filterUpdateCount}
          />
        </div>

        <div>
          {isFiltered && (
            <div className="my-3">
              <div className="d-flex justify-content-between mb-3">
                <h4 className="h4 mx-4">
                  Total selected: {selectedItems?.length}
                </h4>
                <B2Button
                  variant="primary"
                  className="d-block ms-auto btn-outline-light"
                  onClick={() => setIsModalOpen(true)}
                  disabled={selectedItems.length == 0}
                >
                  Create Schedule
                  <FontAwesomeIcon icon={faCalendar} className="fs-7 ms-2" />
                </B2Button>
              </div>

              <TableComponent
                tableHeaders={columns}
                data={data}
                totalNumberOfRecords={totalNumberOfRecords}
                pageSize={pageSize}
                current={current}
                onChangePage={onChangePage}
                onChangeSort={onChangeSort}
                enableSelection={true}
                isLoadingData={isLoading}
              />
            </div>
          )}
        </div>
      </div>

      {/* modals */}
      {isModalOpen && (
        <ModalComponent
          showModal={isModalOpen}
          handleClose={handleCloseModal}
          headerTitle={"Set Schedule"}
          actionConfig={modalActionConfig}
        >
          <FormBuilderComponent
            formRef={formRef}
            initialValues={modalFormInitialValues}
            validation={validationSchema}
            formConfig={formConfig}
          />
        </ModalComponent>
      )}
    </div>
  );
};

export default NewScheduleTableComponent;
